class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onOptionClick.bind(this));
  }

  onOptionClick(event) {
    const universeBtn = event.target.closest('[data-universe-url]');
    if (universeBtn) {
      event.preventDefault();
      window.location.href = universeBtn.getAttribute('data-universe-url');
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length) {
      event.preventDefault();
      return;
    }
    const matchedHandle = window.BingeUniverseResolver && typeof window.BingeUniverseResolver.match === 'function'
      ? window.BingeUniverseResolver.match(query)
      : null;
    if (matchedHandle) {
      event.preventDefault();
      window.location.href = `/collections/${matchedHandle}`;
      return;
    }
    if (this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm && previousTerm.trim().length > 0) {
      try {
        const escaped = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const matches = currentButtonText.match(new RegExp(escaped, 'g'));
        if (matches && matches.length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return;
        }
        if (matches && matches.length === 1) {
          const newButtonText = currentButtonText.replace(previousTerm, newTerm);
          searchForTextElement.innerText = newButtonText;
        }
      } catch (e) {
        // Safe fallback
      }
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) {
      if (selectedOption.hasAttribute('data-universe-url')) {
        window.location.href = selectedOption.getAttribute('data-universe-url');
        return;
      }
      selectedOption.click();
    }
  }

  getUniverseTitle(handle) {
    if (window.BingeUniverseResolver && typeof window.BingeUniverseResolver.getTitle === 'function') {
      const t = window.BingeUniverseResolver.getTitle(handle);
      if (t) return t;
    }
    const map = {
      '90s-sitcom': '90s Sitcom',
      'pirate-king': 'Pirate King',
      'comic-legends': 'Comic Legends',
      'dark-vigilante': 'Dark Vigilante',
      'albuquerque-empire': 'Albuquerque Empire',
      'iron-realm': 'Iron Realm',
      'consulting-detective': 'Consulting Detective',
      'scranton-branch': 'Scranton Branch',
      'breathing-swordsman': 'Breathing Swordsman',
      'walled-kingdom': 'Walled Kingdom',
      'hidden-shinobi': 'Hidden Shinobi',
      'galactic-saga': 'Galactic Saga',
      '80s-supernatural-mystery': '80s Supernatural Mystery',
      'underground-society': 'Underground Society',
      'cursed-buccaneer': 'Cursed Buccaneer',
      'cursed-sorcerers': 'Cursed Sorcerers',
      'flight-of-the-court': 'Flight Of The Court',
      'posters': 'Collector Metal Posters',
    };
    if (map[handle]) return map[handle];
    return handle
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  async buildUniverseSearchResults(handle, searchTerm) {
    const res = await fetch(`/collections/${handle}/products.json?limit=24`, {
      signal: this.abortController.signal,
    });
    if (!res.ok) return null;
    const data = await res.json();
    let products = data.products || [];
    if (products.length === 0) return null;

    // Filter/rank if specific sub-keywords were entered (e.g. "hoodie", "shirt", "sweatshirt", "poster")
    const searchWords = searchTerm
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 2);
    if (searchWords.length > 0) {
      products.sort((a, b) => {
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();
        const aScore = searchWords.reduce((acc, w) => acc + (aTitle.includes(w) ? 1 : 0), 0);
        const bScore = searchWords.reduce((acc, w) => acc + (bTitle.includes(w) ? 1 : 0), 0);
        return bScore - aScore;
      });
    }

    const displayProducts = products.slice(0, 6);
    const collectionTitle = this.getUniverseTitle(handle);
    const currency = window.Shopify?.currency?.active || 'USD';
    const totalCount = displayProducts.length + 1;

    const escapeHtml = (str) =>
      (str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    let productsHtml = '';
    displayProducts.forEach((p, idx) => {
      const pUrl = `/products/${p.handle}`;
      const img = p.images && p.images[0] ? p.images[0].src : '';
      const imgSrc = img ? `${img}${img.includes('?') ? '&' : '?'}width=150` : '';
      const priceVal = p.variants && p.variants[0] ? p.variants[0].price : '';
      const formattedPrice = priceVal ? `$${parseFloat(priceVal).toFixed(2)} ${currency}` : '';

      productsHtml += `
        <li id="predictive-search-option-product-${idx + 1}" class="predictive-search__list-item" role="option" aria-selected="false" data-universe="${handle}">
          <product-component>
            <a href="${pUrl}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
              ${imgSrc ? `<img class="predictive-search__image" src="${imgSrc}" alt="${escapeHtml(p.title)}" width="50" height="50">` : ''}
              <div class="predictive-search__item-content">
                <p class="predictive-search__item-heading h5">${escapeHtml(p.title)}</p>
                ${
                  formattedPrice
                    ? `<div class="price">
                        <div class="price__container">
                          <div class="price__regular">
                            <span class="visually-hidden visually-hidden--inline">Regular price</span>
                            <span class="price-item price-item--regular">${formattedPrice}</span>
                          </div>
                        </div>
                      </div>`
                    : ''
                }
              </div>
            </a>
          </product-component>
        </li>
      `;
    });

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
          <div class="predictive-search__result-group">
            <div>
              <h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">Suggestions</h2>
              <ul id="predictive-search-results-queries-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-queries">
                <li id="predictive-search-option-collection-1" class="predictive-search__list-item" role="option" aria-selected="false">
                  <a href="/collections/${handle}" class="predictive-search__item link link--text" tabindex="-1">
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading h5" style="display:flex;align-items:center;gap:8px;width:100%;">
                        <span style="font-size:1.15em;">📺</span>
                        <span><strong>${escapeHtml(collectionTitle)}</strong> Collection</span>
                        <span style="font-size:0.8em;opacity:0.75;margin-left:auto;white-space:nowrap;">Explore Universe ➔</span>
                      </p>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="predictive-search__result-group">
            <div>
              <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>
              <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                ${productsHtml}
              </ul>
            </div>
          </div>
        </div>

        <div class="predictive-search__loading-state">
          <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
        </div>

        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button type="button" class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false" data-universe-url="/collections/${handle}">
            <span data-predictive-search-search-for-text="">Explore all ${escapeHtml(collectionTitle)} designs</span>
            <span class="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" viewBox="0 0 14 10"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"></path></svg>
            </span>
          </button>
        </div>

        <span class="hidden" data-predictive-search-live-region-count-value="" data-total-results="${totalCount}">
          ${totalCount} results: 1 collection, ${displayProducts.length} products
        </span>
      </div>
    `;
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    // Cancel any previous in-flight request to prevent race conditions
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    // 1. Check if query matches a known universe in BingeUniverseResolver
    const matchedHandle =
      window.BingeUniverseResolver && typeof window.BingeUniverseResolver.match === 'function'
        ? window.BingeUniverseResolver.match(searchTerm)
        : null;

    if (matchedHandle) {
      try {
        const markup = await this.buildUniverseSearchResults(matchedHandle, searchTerm);
        // Verify current query has not changed or been cleared while waiting for network response
        if (this.getQuery().toLowerCase() !== searchTerm.toLowerCase()) {
          return;
        }
        if (markup) {
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = markup;
          });
          this.renderSearchResults(markup);
          searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
          return;
        }
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 20) {
          searchDeferred?.reject(err);
          return;
        }
        console.warn('Universe search error, falling back to standard predictive search:', err);
      }
    }

    // 2. Standard predictive search
    return fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.getQuery().toLowerCase() !== searchTerm.toLowerCase()) {
          return;
        }
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
